// Language Management
class LanguageManager {
    constructor() {
        this.currentLang = localStorage.getItem('language') || 'he';
        this.init();
    }
    
    init() {
        this.setLanguage(this.currentLang);
        this.bindEvents();
    }
    
    bindEvents() {
        const langToggle = document.getElementById('langToggle');
        if (langToggle) {
            langToggle.addEventListener('click', () => this.toggleLanguage());
        }
    }
    
    setLanguage(lang) {
        this.currentLang = lang;
        document.body.setAttribute('data-lang', lang);
        document.documentElement.setAttribute('lang', lang);
        document.documentElement.setAttribute('dir', lang === 'he' ? 'rtl' : 'ltr');
        
        // Update current language indicator
        const currentLangSpan = document.getElementById('currentLang');
        if (currentLangSpan) {
            currentLangSpan.textContent = lang === 'he' ? 'EN' : 'עב';
        }
        
        // Update page title
        const titles = {
            he: 'יונתן ווידן, עורך דין - משרד עורכי דין בכפר סבא',
            en: 'Jonathan Weiden, Attorney - Law Firm in Kfar Saba'
        };
        document.title = titles[lang];
        
        // Store preference
        localStorage.setItem('language', lang);
        
        // Update all translatable elements
        this.updateTranslatableElements();
    }
    
    toggleLanguage() {
        const newLang = this.currentLang === 'he' ? 'en' : 'he';
        this.setLanguage(newLang);
        
        // Track language change analytics
        if (typeof AnalyticsTracker !== 'undefined') {
            AnalyticsTracker.trackLanguageChange(newLang);
        }
    }
    
    updateTranslatableElements() {
        const elements = document.querySelectorAll('[data-he][data-en]');
        elements.forEach(element => {
            const text = element.getAttribute(`data-${this.currentLang}`);
            if (text) {
                // Preserve child elements (like sr-only spans) by only updating text nodes
                const childNodes = Array.from(element.childNodes);
                const textNodes = childNodes.filter(node => node.nodeType === Node.TEXT_NODE);
                const nonTextElements = childNodes.filter(node => node.nodeType === Node.ELEMENT_NODE);
                
                // Clear only text content, preserve element children
                element.innerHTML = '';
                
                // Add back non-text elements first (like sr-only spans)
                nonTextElements.forEach(el => element.appendChild(el));
                
                // Add the translated text
                element.appendChild(document.createTextNode(text));
            }
        });
    }
}

// Mobile Menu Management
class MobileMenu {
    constructor() {
        this.menuToggle = document.getElementById('menuToggle');
        this.nav = document.getElementById('nav');
        this.navList = document.querySelector('.nav-list');
        this.isOpen = false;
        this.init();
    }
    
    init() {
        if (this.menuToggle) {
            this.menuToggle.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggle();
            });
        }
        
        // Close menu when clicking on nav links
        const navLinks = document.querySelectorAll('.nav-link');
        navLinks.forEach(link => {
            link.addEventListener('click', () => this.close());
        });
        
        // Close menu when clicking outside
        document.addEventListener('click', () => {
            if (this.isOpen) {
                this.close();
            }
        });

        if (this.nav) {
            this.nav.addEventListener('click', (e) => {
                e.stopPropagation();
            });
        }
        
        // Handle window resize
        window.addEventListener('resize', () => {
            if (window.innerWidth > 768) {
                this.close();
            }
        });
    }
    
    toggle() {
        this.isOpen ? this.close() : this.open();
    }
    
    open() {
        this.isOpen = true;
        this.menuToggle.classList.add('active');
        this.navList.classList.add('active');
        document.body.style.overflow = 'hidden';
    }
    
    close() {
        this.isOpen = false;
        this.menuToggle.classList.remove('active');
        this.navList.classList.remove('active');
        document.body.style.overflow = '';
    }
}

// Smooth Scrolling and Header Behavior
class ScrollManager {
    constructor() {
        this.header = document.getElementById('header');
        this.backToTop = document.getElementById('backToTop');
        this.init();
    }
    
    init() {
        this.bindScrollEvents();
        this.bindBackToTopEvent();
        this.handleSmoothScrolling();
    }
    
    bindScrollEvents() {
        let lastScrollTop = 0;
        
        window.addEventListener('scroll', () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            // Header hide/show on scroll
            if (this.header) {
                if (scrollTop > lastScrollTop && scrollTop > 100) {
                    // Scrolling down
                    this.header.style.transform = 'translateY(-100%)';
                } else {
                    // Scrolling up
                    this.header.style.transform = 'translateY(0)';
                }
                
                // Add shadow to header when scrolled
                if (scrollTop > 10) {
                    this.header.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                } else {
                    this.header.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.05)';
                }
            }
            
            lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
        });
    }
    
    bindBackToTopEvent() {
        if (this.backToTop) {
            this.backToTop.addEventListener('click', () => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            });
        }
    }
    
    handleSmoothScrolling() {
        // Smooth scrolling for anchor links
        const anchorLinks = document.querySelectorAll('a[href^="#"]');
        anchorLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                const targetId = link.getAttribute('href');
                if (targetId === '#') return;
                
                const targetElement = document.querySelector(targetId);
                if (targetElement) {
                    e.preventDefault();
                    const headerHeight = this.header ? this.header.offsetHeight : 0;
                    const targetPosition = targetElement.offsetTop - headerHeight - 20;
                    
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            });
        });
    }
}

// Contact Form Management
class ContactForm {
    constructor() {
        this.form = document.getElementById('contactForm');
        this.init();
    }
    
    init() {
        if (this.form) {
            this.form.addEventListener('submit', (e) => this.handleSubmit(e));
        }
    }
    
    handleSubmit(e) {
        e.preventDefault();
        
        const formData = new FormData(this.form);
        const data = Object.fromEntries(formData);
        
        // Basic validation
        if (!this.validateForm(data)) {
            // Track failed form submission
            if (typeof AnalyticsTracker !== 'undefined') {
                AnalyticsTracker.trackFormSubmission('contact_form', false);
            }
            return;
        }
        
        // Track successful form submission
        if (typeof AnalyticsTracker !== 'undefined') {
            AnalyticsTracker.trackFormSubmission('contact_form', true);
        }
        
        // Create WhatsApp message
        const message = this.createWhatsAppMessage(data);
        const whatsappUrl = `https://wa.me/972544934247?text=${encodeURIComponent(message)}`;
        
        // Open WhatsApp
        window.open(whatsappUrl, '_blank');
        
        // Show success message
        this.showSuccessMessage();
        
        // Reset form
        this.form.reset();
    }
    
    validateForm(data) {
        let isValid = true;
        const requiredFields = ['name', 'email', 'message'];
        
        requiredFields.forEach(field => {
            const input = this.form.querySelector(`[name="${field}"]`);
            const value = data[field]?.trim();
            
            if (!value) {
                this.showFieldError(input, 'זהו שדה חובה / This field is required');
                isValid = false;
            } else {
                this.clearFieldError(input);
            }
        });
        
        // Email validation
        if (data.email && !this.isValidEmail(data.email)) {
            const emailInput = this.form.querySelector('[name="email"]');
            this.showFieldError(emailInput, 'כתובת אימייל לא תקינה / Invalid email address');
            isValid = false;
        }
        
        return isValid;
    }
    
    showFieldError(input, message) {
        this.clearFieldError(input);
        input.style.borderColor = 'var(--error)';
        
        const errorDiv = document.createElement('div');
        errorDiv.className = 'field-error';
        errorDiv.style.color = 'var(--error)';
        errorDiv.style.fontSize = 'var(--text-sm)';
        errorDiv.style.marginTop = 'var(--spacing-1)';
        errorDiv.textContent = message;
        
        input.parentNode.appendChild(errorDiv);
    }
    
    clearFieldError(input) {
        input.style.borderColor = '';
        const errorDiv = input.parentNode.querySelector('.field-error');
        if (errorDiv) {
            errorDiv.remove();
        }
    }
    
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }
    
    createWhatsAppMessage(data) {
        const currentLang = document.body.getAttribute('data-lang');
        
        if (currentLang === 'he') {
            return `שלום יונתן,
אני מעוניין/ת בייעוץ משפטי.

פרטים:
שם: ${data.name}
אימייל: ${data.email}
טלפון: ${data.phone || 'לא צוין'}

הודעה:
${data.message}

תודה!`;
        } else {
            return `Hello Jonathan,
I'm interested in legal consultation.

Details:
Name: ${data.name}
Email: ${data.email}
Phone: ${data.phone || 'Not provided'}

Message:
${data.message}

Thank you!`;
        }
    }
    
    showSuccessMessage() {
        const currentLang = document.body.getAttribute('data-lang');
        const message = currentLang === 'he' 
            ? 'ההודעה נשלחה בהצלחה! נפתח עבורכם חלון וואטסאפ.'
            : 'Message sent successfully! WhatsApp window will open.';
        
        // Create and show success notification
        const notification = document.createElement('div');
        notification.className = 'success-notification';
        notification.style.cssText = `
            position: fixed;
            top: 100px;
            right: 20px;
            background: var(--success);
            color: white;
            padding: 1rem 1.5rem;
            border-radius: var(--radius-lg);
            box-shadow: var(--shadow-lg);
            z-index: 1001;
            font-weight: 500;
            animation: slideInRight 0.3s ease-out;
        `;
        notification.textContent = message;
        
        document.body.appendChild(notification);
        
        // Remove notification after 5 seconds
        setTimeout(() => {
            notification.style.animation = 'slideOutRight 0.3s ease-in';
            setTimeout(() => notification.remove(), 300);
        }, 5000);
    }
}

// Animation on Scroll
class ScrollAnimations {
    constructor() {
        this.observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };
        this.init();
    }
    
    init() {
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
                (entries) => this.handleIntersection(entries),
                this.observerOptions
            );
            
            this.observeElements();
        }
    }
    
    observeElements() {
        const elementsToAnimate = document.querySelectorAll(
            '.service-card, .feature, .about-content, .contact-item, .hero-content'
        );
        
        elementsToAnimate.forEach(el => {
            el.style.opacity = '0';
            el.style.transform = 'translateY(30px)';
            el.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
            this.observer.observe(el);
        });
    }
    
    handleIntersection(entries) {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.style.opacity = '1';
                entry.target.style.transform = 'translateY(0)';
                this.observer.unobserve(entry.target);
            }
        });
    }
}

// Utility Functions
class Utils {
    static formatPhoneNumber(phone) {
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        
        // Format Israeli phone numbers
        if (cleaned.startsWith('972')) {
            return '+' + cleaned;
        } else if (cleaned.startsWith('0')) {
            return '+972' + cleaned.substring(1);
        }
        
        return phone;
    }
    
    static copyToClipboard(text) {
        if (navigator.clipboard && window.isSecureContext) {
            return navigator.clipboard.writeText(text);
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            
            try {
                document.execCommand('copy');
                textArea.remove();
                return Promise.resolve();
            } catch (err) {
                textArea.remove();
                return Promise.reject(err);
            }
        }
    }
    
    static debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
}

// Performance Monitoring
class PerformanceMonitor {
    static logPageLoad() {
        window.addEventListener('load', () => {
            if ('performance' in window) {
                const perfData = performance.getEntriesByType('navigation')[0];
                console.log('Page Load Time:', perfData.loadEventEnd - perfData.loadEventStart + 'ms');
                console.log('DOM Content Loaded:', perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart + 'ms');
            }
        });
    }
}

// Analytics Event Tracking
class AnalyticsTracker {
    static trackEvent(eventName, eventData = {}) {
        // Check if gtag is available
        if (typeof gtag !== 'undefined') {
            gtag('event', eventName, {
                event_category: eventData.category || 'engagement',
                event_label: eventData.label || '',
                value: eventData.value || 1,
                ...eventData
            });
            console.log('Analytics event tracked:', eventName, eventData);
        } else {
            console.log('Analytics tracking attempted but gtag not available:', eventName, eventData);
        }
    }
    
    static trackButtonClick(buttonText, buttonType, location) {
        this.trackEvent('button_click', {
            category: 'user_interaction',
            label: buttonText,
            button_type: buttonType,
            location: location
        });
    }
    
    static trackPhoneCall(phoneNumber, location) {
        this.trackEvent('phone_call_initiated', {
            category: 'contact',
            label: phoneNumber,
            location: location
        });
    }
    
    static trackWhatsAppClick(location) {
        this.trackEvent('whatsapp_click', {
            category: 'contact',
            label: 'WhatsApp Contact',
            location: location
        });
    }
    
    static trackEmailClick(email, location) {
        this.trackEvent('email_click', {
            category: 'contact',
            label: email,
            location: location
        });
    }
    
    static trackLanguageChange(newLanguage) {
        this.trackEvent('language_change', {
            category: 'user_preference',
            label: newLanguage
        });
    }
    
    static trackSectionView(sectionName) {
        this.trackEvent('section_view', {
            category: 'navigation',
            label: sectionName
        });
    }
    
    static trackFormSubmission(formType, success = true) {
        this.trackEvent('form_submission', {
            category: 'contact',
            label: formType,
            success: success
        });
    }
}

// Event Tracker for all interactive elements
class EventTracker {
    constructor() {
        this.init();
    }
    
    init() {
        this.trackButtonClicks();
        this.trackPhoneCalls();
        this.trackWhatsAppClicks();
        this.trackEmailClicks();
        this.trackNavigationClicks();
        this.trackSectionViews();
    }
    
    trackButtonClicks() {
        // Track all button clicks
        document.addEventListener('click', (e) => {
            const button = e.target.closest('button, .btn, [role="button"]');
            if (button) {
                const buttonText = button.textContent.trim();
                const buttonType = button.className.includes('btn-primary') ? 'primary' :
                                 button.className.includes('btn-secondary') ? 'secondary' :
                                 button.className.includes('btn-whatsapp') ? 'whatsapp' :
                                 'other';
                const location = this.getElementLocation(button);
                
                AnalyticsTracker.trackButtonClick(buttonText, buttonType, location);
            }
        });
    }
    
    trackPhoneCalls() {
        // Track phone number clicks
        document.addEventListener('click', (e) => {
            const phoneLink = e.target.closest('a[href^="tel:"]');
            if (phoneLink) {
                const phoneNumber = phoneLink.getAttribute('href').replace('tel:', '');
                const location = this.getElementLocation(phoneLink);
                
                AnalyticsTracker.trackPhoneCall(phoneNumber, location);
            }
        });
    }
    
    trackWhatsAppClicks() {
        // Track WhatsApp clicks
        document.addEventListener('click', (e) => {
            const whatsappLink = e.target.closest('a[href*="wa.me"], a[href*="whatsapp"], .whatsapp-floating-btn');
            if (whatsappLink) {
                const location = this.getElementLocation(whatsappLink);
                AnalyticsTracker.trackWhatsAppClick(location);
            }
        });
    }
    
    trackEmailClicks() {
        // Track email clicks
        document.addEventListener('click', (e) => {
            const emailLink = e.target.closest('a[href^="mailto:"]');
            if (emailLink) {
                const email = emailLink.getAttribute('href').replace('mailto:', '');
                const location = this.getElementLocation(emailLink);
                
                AnalyticsTracker.trackEmailClick(email, location);
            }
        });
    }
    
    trackNavigationClicks() {
        // Track navigation menu clicks
        document.addEventListener('click', (e) => {
            const navLink = e.target.closest('.nav-link');
            if (navLink) {
                const sectionName = navLink.textContent.trim();
                const href = navLink.getAttribute('href');
                
                AnalyticsTracker.trackEvent('navigation_click', {
                    category: 'navigation',
                    label: sectionName,
                    target_section: href
                });
            }
        });
    }
    
    trackSectionViews() {
        // Track section views using Intersection Observer
        const sections = document.querySelectorAll('section[id]');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
                    const sectionName = entry.target.id;
                    AnalyticsTracker.trackSectionView(sectionName);
                }
            });
        }, {
            threshold: 0.5
        });
        
        sections.forEach(section => observer.observe(section));
    }
    
    getElementLocation(element) {
        // Determine the location context of an element
        const section = element.closest('section');
        const header = element.closest('header');
        const footer = element.closest('footer');
        
        if (header) return 'header';
        if (footer) return 'footer';
        if (section && section.id) return section.id;
        
        // Check for specific component classes
        if (element.closest('.hero')) return 'hero';
        if (element.closest('.about')) return 'about';
        if (element.closest('.services')) return 'services';
        if (element.closest('.contact')) return 'contact';
        if (element.closest('.whatsapp-floating-btn')) return 'floating_whatsapp';
        
        return 'unknown';
    }
}

// SEO and Analytics Helper
class SEOHelper {
    static updateMetaTags(lang) {
        const metaData = {
            he: {
                description: 'עורך דין יונתן ווידן - משרד עורכי דין מקצועי בכפר סבא. שירותי ייעוץ משפטי, ייעוץ מיסים, ייעוץ עסקי. ניסיון רב בתחום החשבונאות והמיסים.',
                keywords: 'עורך דין כפר סבא, משרד עורכי דין, יונתן ווידן, ייעוץ משפטי, ייעוץ מיסים, רואה חשבון, עורך דין עסקי, משפט מיסים'
            },
            en: {
                description: 'Attorney Jonathan Weiden - Professional law firm in Kfar Saba. Legal consultation, tax consultation, business consultation services. Extensive experience in accounting and taxation.',
                keywords: 'attorney Kfar Saba, law firm, Jonathan Weiden, legal consultation, tax consultation, accountant, business lawyer, tax law'
            }
        };
        
        // Update meta description
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.content = metaData[lang].description;
        }
        
        // Update meta keywords
        const metaKeywords = document.querySelector('meta[name="keywords"]');
        if (metaKeywords) {
            metaKeywords.content = metaData[lang].keywords;
        }
    }
}

// Error Handling
class ErrorHandler {
    static init() {
        window.addEventListener('error', (e) => {
            console.error('JavaScript Error:', e.error);
            // In production, you might want to send this to an error tracking service
        });
        
        window.addEventListener('unhandledrejection', (e) => {
            console.error('Unhandled Promise Rejection:', e.reason);
            // In production, you might want to send this to an error tracking service
        });
    }
}

// Initialize Application
class App {
    constructor() {
        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initializeModules());
        } else {
            this.initializeModules();
        }
    }
    
    initializeModules() {
        try {
            // Initialize error handling first
            ErrorHandler.init();
            
            // Initialize core modules
            this.languageManager = new LanguageManager();
            this.mobileMenu = new MobileMenu();
            this.scrollManager = new ScrollManager();
            this.contactForm = new ContactForm();
            this.scrollAnimations = new ScrollAnimations();
            
            // Initialize event tracking for analytics
            this.eventTracker = new EventTracker();
            
            // Initialize performance monitoring
            PerformanceMonitor.logPageLoad();
            
            // Add smooth animations
            this.addSmoothAnimations();
            
            // Add keyboard navigation
            this.addKeyboardNavigation();
            
            console.log('Law Firm Website initialized successfully');
        } catch (error) {
            console.error('Error initializing application:', error);
        }
    }
    
    addSmoothAnimations() {
        // Add CSS animations
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideInRight {
                from { transform: translateX(300px); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
            }
            
            @keyframes slideOutRight {
                from { transform: translateX(0); opacity: 1; }
                to { transform: translateX(300px); opacity: 0; }
            }
            
            @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.05); }
            }
            
            .pulse-animation {
                animation: pulse 2s infinite;
            }
        `;
        document.head.appendChild(style);
    }
    
    addKeyboardNavigation() {
        // Enhanced keyboard navigation
        document.addEventListener('keydown', (e) => {
            // Escape key closes mobile menu
            if (e.key === 'Escape' && this.mobileMenu.isOpen) {
                this.mobileMenu.close();
            }
            
            // Enter key activates buttons
            if (e.key === 'Enter' && e.target.matches('button, [role="button"]')) {
                e.target.click();
            }
        });
    }
}

// Start the application
new App();

// Export for potential testing
if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        LanguageManager,
        MobileMenu,
        ScrollManager,
        ContactForm,
        Utils,
        App
    };
}
